// ============================================================================
// ISMAN — Live CMS integration (frontend side)
// Reads PUBLISHED page layouts saved by the admin Web Editor (page_layouts)
// and exposes them to the public site. Safe by design: if no published layout
// exists for a slug, hooks return null so the existing hardcoded page renders.
// ============================================================================
import { useEffect, useState } from "react";
import { supabase as cmsClient } from "@/lib/supabase";

export { cmsClient };

// ---- Block schema: MUST match the admin BlockRenderer (BuilderElement) ----
export type BlockType =
  | "section" | "column" | "text" | "heading" | "image" | "button"
  | "divider" | "spacer" | "video" | "form" | "map" | "testimonial" | "social";

export interface AnimationSettings {
  type:
    | "none" | "fade-in" | "slide-up" | "slide-down" | "slide-left" | "slide-right"
    | "zoom-in" | "zoom-out" | "flip-x" | "flip-y" | "bounce" | "pulse";
  duration: "fast" | "normal" | "slow";
  delay: number;
  timing: "ease" | "ease-in" | "ease-out" | "ease-in-out" | "linear";
  trigger: "load" | "scroll" | "hover";
  repeat: boolean;
}

export interface BuilderElement {
  id: string;
  type: BlockType;
  content?: string;
  src?: string;
  href?: string;
  align?: "left" | "center" | "right";
  children?: BuilderElement[];
  fields?: { label: string; type: "text" | "email" | "textarea" }[];
  lat?: string;
  lng?: string;
  zoom?: number;
  author?: string;
  role?: string;
  rating?: number;
  links?: { platform: string; url: string }[];
  animation?: AnimationSettings;
}

export interface PublishedLayout {
  page_slug: string;
  name: string | null;
  layout_json: BuilderElement[];
  is_published: boolean;
  updated_at: string;
}

// Normalise a path like "/hvac-systems" -> "hvac-systems"; "/" -> "home".
export function slugFromPath(pathname: string): string {
  const clean = pathname.replace(/^\/+|\/+$/g, "");
  return clean === "" ? "home" : clean;
}

/**
 * Returns the PUBLISHED layout for a slug, or null when none exists
 * (so the caller can fall back to the existing hardcoded page).
 * `null` = not yet loaded; `false` = loaded, nothing published.
 */
export function usePublishedLayout(slug: string) {
  const [state, setState] = useState<{
    loading: boolean;
    layout: BuilderElement[] | null;
    found: boolean;
  }>({ loading: true, layout: null, found: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await cmsClient
          .from("page_layouts")
          .select("page_slug, layout_json, is_published, updated_at")
          .eq("page_slug", slug)
          .eq("is_published", true)
          .maybeSingle();
        if (!alive) return;
        if (error || !data) {
          setState({ loading: false, layout: null, found: false });
          return;
        }
        const layout = (data.layout_json as BuilderElement[]) || [];
        setState({ loading: false, layout, found: layout.length > 0 });
      } catch {
        if (alive) setState({ loading: false, layout: null, found: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  return state;
}
